import React, { useState, useEffect } from "react";
import {
  ExpenseFormData,
  ExpenseCategory,
  TravelExpense,
} from "../types/expense";

// Utility function to get file display information with fallback for legacy data
const getFileDisplayInfo = (expense: TravelExpense) => {
  if (expense.receiptMetadata) {
    return {
      name: expense.receiptMetadata.name,
      size: expense.receiptMetadata.size,
    };
  }

  if (expense.receipt && typeof expense.receipt === "object") {
    return {
      name: expense.receipt.name,
      size: expense.receipt.size,
    };
  }

  if (expense.receipt && typeof expense.receipt === "string") {
    return {
      name: expense.receipt,
      size: null,
    };
  }

  return null;
};

interface EditExpenseModalProps {
  expense: TravelExpense;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, updatedData: ExpenseFormData) => Promise<void>;
}

const EditExpenseModal: React.FC<EditExpenseModalProps> = ({
  expense,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<ExpenseFormData>({
    date: expense.date,
    description: expense.description,
    category: expense.category,
    amount: expense.amount.toString(),
    location: expense.location,
    duration: expense.duration || "",
    receipt: undefined,
  });

  const [errors, setErrors] = useState<Partial<ExpenseFormData>>({});
  const [keepExistingReceipt, setKeepExistingReceipt] = useState(true);

  // Update form data when expense prop changes
  useEffect(() => {
    setFormData({
      date: expense.date,
      description: expense.description,
      category: expense.category,
      amount: expense.amount.toString(),
      location: expense.location,
      duration: expense.duration || "",
      receipt: undefined,
    });
    setKeepExistingReceipt(true);
    setErrors({});
  }, [expense]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ExpenseFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFormData((prev) => ({
      ...prev,
      receipt: file,
    }));
    if (file) {
      setKeepExistingReceipt(false);
    }
  };

  // Custom File Upload Component
  const CustomFileUpload = ({
    onChange,
  }: {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleClick = () => {
      fileInputRef.current?.click();
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        const event = {
          target: {
            files: files,
          },
        } as any;
        onChange(event);
      }
    };

    return (
      <div className="custom-file-upload">
        <input
          ref={fileInputRef}
          type="file"
          onChange={onChange}
          accept=".pdf,.jpg,.jpeg,.png,.gif"
          className="hidden-file-input"
        />
        <div
          className="file-upload-area"
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="file-upload-content">
            <i className="fas fa-cloud-upload-alt file-upload-icon"></i>
            <div className="file-upload-text">
              <span className="file-upload-main">
                Beleg auswählen oder hierher ziehen
              </span>
              <span className="file-upload-sub">
                PDF, JPG, PNG, GIF bis 10MB
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleCategoryChange = (category: ExpenseCategory) => {
    setFormData((prev) => ({
      ...prev,
      category,
    }));
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ExpenseFormData> = {};

    // Description is required for both categories
    if (!formData.description.trim()) {
      newErrors.description = "Beschreibung ist erforderlich";
    }

    // Amount is required for both categories
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Betrag muss größer als 0 sein";
    }

    // Additional validation for Spesenabrechnung
    if (formData.category === ExpenseCategory.SPESENABRECHNUNG) {
      if (!formData.location.trim()) {
        newErrors.location = "Ort ist erforderlich";
      }

      if (!formData.date) {
        newErrors.date = "Datum ist erforderlich";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      const updatedFormData = {
        ...formData,
        receipt: keepExistingReceipt ? expense.receipt : formData.receipt,
        receiptMetadata: keepExistingReceipt
          ? expense.receiptMetadata
          : formData.receipt
            ? {
                name: formData.receipt.name,
                size: formData.receipt.size,
                type: formData.receipt.type,
                lastModified: formData.receipt.lastModified,
              }
            : undefined,
      };
      try {
        await onSave(expense.id, updatedFormData);
        onClose();
      } catch (error) {
        console.error("Failed to save expense:", error);
        // Error is already handled in the parent component
      }
    }
  };

  const handleRemoveReceipt = () => {
    setFormData((prev) => ({
      ...prev,
      receipt: undefined,
    }));
    setKeepExistingReceipt(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Ausgabe bearbeiten</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Category Selection */}
          <div className="form-group">
            <label htmlFor="category">Kategorie</label>
            <div className="category-switch">
              <button
                type="button"
                className={`switch-option ${
                  formData.category === ExpenseCategory.BELEGE ? "active" : ""
                }`}
                onClick={() => handleCategoryChange(ExpenseCategory.BELEGE)}
              >
                Belege
              </button>
              <button
                type="button"
                className={`switch-option ${
                  formData.category === ExpenseCategory.SPESENABRECHNUNG
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  handleCategoryChange(ExpenseCategory.SPESENABRECHNUNG)
                }
              >
                Spesenabrechnung
              </button>
            </div>
          </div>

          {/* Description field - shown for both categories */}
          <div className="form-group">
            <label htmlFor="description">Beschreibung *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Ausgabenbeschreibung eingeben..."
              className={errors.description ? "error" : ""}
              rows={3}
              required
            />
            {errors.description && (
              <span className="error-message">{errors.description}</span>
            )}
          </div>

          {/* Fields for Belege category */}
          {formData.category === ExpenseCategory.BELEGE && (
            <>
              <div className="form-group">
                <label htmlFor="amount">Preis (EUR) *</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={errors.amount ? "error" : ""}
                  required
                />
                {errors.amount && (
                  <span className="error-message">{errors.amount}</span>
                )}
              </div>
            </>
          )}

          {/* Fields for Spesenabrechnung category */}
          {formData.category === ExpenseCategory.SPESENABRECHNUNG && (
            <>
              <div className="form-group">
                <label htmlFor="date">Datum *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className={errors.date ? "error" : ""}
                  required
                />
                {errors.date && (
                  <span className="error-message">{errors.date}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="location">Ort *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Ort eingeben..."
                  className={errors.location ? "error" : ""}
                  required
                />
                {errors.location && (
                  <span className="error-message">{errors.location}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="duration">Dauer</label>
                <input
                  type="text"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder="z.B. 2 Stunden, 1 Tag, 3 Nächte..."
                  className={errors.duration ? "error" : ""}
                />
                {errors.duration && (
                  <span className="error-message">{errors.duration}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="amount">Betrag (EUR) *</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={errors.amount ? "error" : ""}
                  required
                />
                {errors.amount && (
                  <span className="error-message">{errors.amount}</span>
                )}
              </div>
            </>
          )}

          {/* Receipt Management */}
          <div className="form-group">
            <label htmlFor="receipt">Beleg</label>

            {/* Show existing receipt info */}
            {(expense.receipt || expense.receiptMetadata) &&
              keepExistingReceipt &&
              (() => {
                const fileInfo = getFileDisplayInfo(expense);
                return fileInfo ? (
                  <div className="existing-receipt">
                    <div className="existing-receipt-info">
                      <i className="fas fa-paperclip"></i>
                      <span>{fileInfo.name}</span>
                      {fileInfo.size && (
                        <span className="file-size">
                          ({(fileInfo.size / 1024).toFixed(1)} KB)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="remove-receipt-btn"
                      onClick={handleRemoveReceipt}
                      title="Beleg entfernen"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : null;
              })()}

            {/* File upload input */}
            <CustomFileUpload onChange={handleFileChange} />

            {/* Show new file preview */}
            {formData.receipt && !keepExistingReceipt && (
              <div className="file-preview">
                <i className="fas fa-file-alt file-preview-icon"></i>
                <div className="file-preview-details">
                  <span className="file-name">{formData.receipt.name}</span>
                  <span className="file-size">
                    ({(formData.receipt.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, receipt: undefined }))
                  }
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="save-btn">
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditExpenseModal;
